import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Mail as MailIcon,
  LocalOffer as CouponIcon,
  MarkEmailRead as MarkEmailReadIcon,
} from '@mui/icons-material';
import { subscribeToNewsletter } from '../services/emailMarketingService';
import { formatApiError } from '../utils/apiError';
import { buildSubscribePayload } from '../utils/newsletter';

interface NewsletterPopupProps {
  open: boolean;
  onClose: () => void;
}

const NewsletterPopup: React.FC<NewsletterPopupProps> = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await subscribeToNewsletter(buildSubscribePayload({ email, name, source: 'popup' }));
      setSuccess(true);
      // Ne jöjjön fel újra, ha már feliratkozott.
      localStorage.setItem('newsletter_popup_shown', 'true');
    } catch (err: unknown) {
      setError(formatApiError(err, { email: 'E-mail cím' }));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Mark as shown in localStorage so it doesn't appear again
    localStorage.setItem('newsletter_popup_shown', 'true');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '18px',
          backgroundColor: 'background.paper',
          color: 'text.primary',
          overflow: 'hidden',
        }
      }}
    >
      {/* Ink-black felső sáv — Studio accent */}
      <Box
        sx={{
          backgroundColor: '#14161F',
          color: '#ffffff',
          px: 3,
          py: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <MailIcon sx={{ color: '#C7351F' }} />
          <Typography
            sx={{
              fontFamily: '"Figtree", sans-serif',
              fontWeight: 600,
              fontSize: '1.125rem',
              letterSpacing: '-0.01em',
            }}
          >
            Iratkozz fel a hírlevelünkre!
          </Typography>
        </Box>
        <IconButton
          aria-label="Bezárás"
          onClick={handleClose}
          size="small"
          sx={{ color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#ffffff' } }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        {!success ? (
          <Box>
            {/* Coupon "hero" — warm cream kártya terrakotta accent */}
            <Box sx={{
              backgroundColor: '#FDE7E3',
              p: 3,
              borderRadius: '14px',
              mb: 3,
              textAlign: 'center',
            }}>
              <CouponIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography
                variant="overline"
                sx={{ display: 'block', color: 'primary.main', letterSpacing: '0.12em', mb: 0.5 }}
              >
                Első rendelés
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Figtree", sans-serif',
                  fontWeight: 700,
                  fontSize: '2rem',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.05,
                  color: 'text.primary',
                }}
              >
                10% kedvezmény
              </Typography>
            </Box>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
              Értesülj elsőként az újdonságokról, exkluzív ajánlatokról és különleges akcióinkról.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Név (opcionális)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
                variant="outlined"
              />

              <TextField
                fullWidth
                required
                type="email"
                label="E-mail cím"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                variant="outlined"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderRadius: 99,
                  backgroundColor: '#14161F',
                  fontFamily: '"Figtree", sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  '&:hover': { backgroundColor: '#2f2e29' },
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Feliratkozom'}
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                A feliratkozással elfogadod az adatkezelési tájékoztatónkat.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <MarkEmailReadIcon sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
            <Typography
              sx={{
                fontFamily: '"Figtree", sans-serif',
                fontWeight: 700,
                fontSize: '1.75rem',
                letterSpacing: '-0.02em',
                mb: 1.5,
                color: 'text.primary',
              }}
            >
              Nézd meg a postafiókodat!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Megerősítő e-mailt küldtünk — a megerősítés után megkapod a 10%-os kuponodat.
            </Typography>

            <Box sx={{
              backgroundColor: '#FDE7E3',
              p: 2.5,
              borderRadius: '14px',
              mb: 2,
            }}>
              <Typography variant="body2" color="text.primary">
                Kattints a levélben lévő <strong>„Feliratkozás megerősítése"</strong> gombra. A kupon
                személyes, 30 napig érvényes, és az első rendelésednél használhatod.
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary">
              Nem érkezett meg pár percen belül? Nézd meg a Spam / Promóciók mappát is.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {success && (
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              backgroundColor: '#14161F',
              borderRadius: 99,
              px: 4,
              fontFamily: '"Figtree", sans-serif',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#2f2e29' },
            }}
          >
            Bezárás
          </Button>
        </DialogActions>
      )}

      {!success && (
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              fontFamily: '"Figtree", sans-serif',
              fontWeight: 500,
              '&:hover': { color: 'primary.main', backgroundColor: 'transparent' },
            }}
          >
            Nem, köszönöm
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default NewsletterPopup;
